import React from 'react';

/**
 * ChartSkeleton Component
 *
 * Content-aware loading skeleton for chart widgets.
 * Provides a better user experience than spinners by showing
 * the approximate layout of the chart while data is loading.
 *
 * Features:
 * - Shimmer animation effect
 * - Realistic chart structure (Y-axis, chart area, X-axis)
 * - Glassmorphism styling to match widget design
 * - Smooth pulse animation
 */
function ChartSkeleton({ height = 300 }) {
  return (
    <div className="chart-skeleton" style={{ height: `${height}px` }}>
      <div className="p-4 space-y-3">
        {/* Chart Header (Title placeholder) */}
        <div className="flex items-center justify-between mb-4">
          <div className="skeleton-bar h-6 w-32 rounded"></div>
          <div className="skeleton-bar h-6 w-20 rounded"></div>
        </div>

        {/* Chart Area */}
        <div className="flex gap-2">
          {/* Y-Axis Labels */}
          <div className="flex flex-col justify-between py-2">
            {[...Array(5)].map((_, i) => (
              <div
                key={`y-${i}`}
                className="skeleton-bar h-3 w-12 rounded"
              ></div>
            ))}
          </div>

          {/* Main Chart Area with Shimmer */}
          <div className="flex-1 relative overflow-hidden">
            <div className="skeleton-shimmer absolute inset-0 rounded-lg"></div>

            {/* Simulated chart line */}
            <svg className="w-full h-full opacity-10" viewBox="0 0 100 60">
              <path
                d="M 0,30 Q 20,10 40,25 T 80,20 T 100,35"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                className="text-blue-400"
              />
            </svg>
          </div>
        </div>

        {/* X-Axis Labels */}
        <div className="flex justify-between mt-2">
          {[...Array(6)].map((_, i) => (
            <div
              key={`x-${i}`}
              className="skeleton-bar h-3 w-16 rounded"
            ></div>
          ))}
        </div>
      </div>

      {/* CSS Styles */}
      <style jsx>{`
        .chart-skeleton {
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.3);
          border-radius: 0.5rem;
          overflow: hidden;
        }

        .skeleton-bar {
          background: linear-gradient(
            90deg,
            rgba(51, 65, 85, 0.6) 0%,
            rgba(71, 85, 105, 0.8) 50%,
            rgba(51, 65, 85, 0.6) 100%
          );
          background-size: 200% 100%;
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .skeleton-shimmer {
          background: linear-gradient(
            110deg,
            rgba(30, 41, 59, 0) 0%,
            rgba(30, 41, 59, 0) 40%,
            rgba(71, 85, 105, 0.3) 50%,
            rgba(30, 41, 59, 0) 60%,
            rgba(30, 41, 59, 0) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 2.5s linear infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Mini Chart Skeleton (for hover previews)
 */
export function MiniChartSkeleton() {
  return (
    <div className="mini-chart-skeleton">
      <div className="p-2 space-y-2">
        {/* Price placeholder */}
        <div className="skeleton-bar h-4 w-20 rounded"></div>

        {/* Mini chart area */}
        <div className="skeleton-shimmer h-16 rounded"></div>

        {/* Date range */}
        <div className="skeleton-bar h-3 w-16 rounded mx-auto"></div>
      </div>

      <style jsx>{`
        .mini-chart-skeleton {
          background: rgba(15, 23, 42, 0.95);
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 0.5rem;
          width: 200px;
        }

        .skeleton-bar {
          background: linear-gradient(
            90deg,
            rgba(51, 65, 85, 0.6) 0%,
            rgba(71, 85, 105, 0.8) 50%,
            rgba(51, 65, 85, 0.6) 100%
          );
          background-size: 200% 100%;
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .skeleton-shimmer {
          background: linear-gradient(
            90deg,
            rgba(51, 65, 85, 0.4) 0%,
            rgba(71, 85, 105, 0.6) 50%,
            rgba(51, 65, 85, 0.4) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 2s linear infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Table Skeleton (for data tables)
 */
export function TableSkeleton({ rows = 5, columns = 4 }) {
  return (
    <div className="table-skeleton p-4 space-y-3">
      {/* Header Row */}
      <div className="flex gap-4 pb-3 border-b border-slate-700">
        {[...Array(columns)].map((_, i) => (
          <div
            key={`header-${i}`}
            className="skeleton-bar h-4 rounded"
            style={{ flex: i === 0 ? 2 : 1 }}
          ></div>
        ))}
      </div>

      {/* Data Rows */}
      {[...Array(rows)].map((_, rowIdx) => (
        <div key={`row-${rowIdx}`} className="flex gap-4">
          {[...Array(columns)].map((_, colIdx) => (
            <div
              key={`cell-${rowIdx}-${colIdx}`}
              className="skeleton-bar h-8 rounded"
              style={{ flex: colIdx === 0 ? 2 : 1 }}
            ></div>
          ))}
        </div>
      ))}

      <style jsx>{`
        .table-skeleton {
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.3);
          border-radius: 0.5rem;
        }

        .skeleton-bar {
          background: linear-gradient(
            90deg,
            rgba(51, 65, 85, 0.6) 0%,
            rgba(71, 85, 105, 0.8) 50%,
            rgba(51, 65, 85, 0.6) 100%
          );
          background-size: 200% 100%;
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}

export default ChartSkeleton;
